import { Component, computed, effect, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { QualityScoreHistoryDataResponse } from 'dashboard/components/visuals-dashboard/widgets/quality-score-history-widget/quality-score-history-data-response.interface';
import { mapLongIceDateToDisplayDate } from 'dashboard/helpers/ice-date-mapper';
import { DashboardWidget } from 'dashboard/interfaces/visuals/dashboard-widget.interface';
import { VisualsDashboardDataService } from 'dashboard/services/visuals-dashboard/visualds-dashboard-data.service';
import {
    ChartSeries,
    IceVisualsDashboardLineChartComponent,
} from 'shared/components/ice-visuals-dashboard/ice-visuals-dashboard-line-chart/ice-visuals-dashboard-line-chart.component';

const SERIES_CONFIG: { name: string; color: string }[] = [
    { name: 'dashboard.visuals.widgets.qualityScoreHistory.series.qualityScore', color: '#3B82F6' },
    { name: 'dashboard.visuals.widgets.qualityScoreHistory.series.sizeScore', color: '#10B981' },
    { name: 'dashboard.visuals.widgets.qualityScoreHistory.series.categoryScore', color: '#F59E0B' },
    { name: 'dashboard.visuals.widgets.qualityScoreHistory.series.roomScore', color: '#EF4444' },
    { name: 'dashboard.visuals.widgets.qualityScoreHistory.series.totalScore', color: '#8B5CF6' },
    { name: 'dashboard.visuals.widgets.qualityScoreHistory.series.bookingCom', color: '#181818' },
];

@Component({
    selector: 'app-quality-score-history-widget',
    imports: [IceVisualsDashboardLineChartComponent, TranslateModule],
    templateUrl: './quality-score-history-widget.component.html',
    styleUrl: './quality-score-history-widget.component.scss',
    providers: [VisualsDashboardDataService],
})
export class QualityScoreHistoryWidgetComponent {
    widget = input.required<DashboardWidget>();
    pickedPeriodValue = input<number | null>(null);
    onLangChange = toSignal(this.translate.onLangChange);

    widgetId = computed(() => {
        const widget = this.widget();
        return widget.iceDashboardWidget.widgetId;
    });

    seriesConfig = computed(() => {
        this.onLangChange(); // Trigger recomputation on language change
        return SERIES_CONFIG.map((config) => ({
            name: this.translate.instant(config.name),
            color: config.color,
        }));
    });

    chartLabels = computed<string[]>(() => {
        const widgetData = this.dataService.data();
        if (!widgetData) {
            return [];
        }
        return widgetData.labels.map((label) => mapLongIceDateToDisplayDate(label));
    });

    chartSeries = computed<ChartSeries[]>(() => {
        const widgetData = this.dataService.data();
        const seriesConfig = this.seriesConfig();
        if (!widgetData) {
            return [];
        }

        return widgetData.dataSets.map((dataSet, index) => {
            const config = seriesConfig[index] ?? { name: `Series ${index + 1}`, color: '#6B7280' };
            return {
                name: config.name,
                data: dataSet.data,
                color: config.color,
            };
        });
    });

    constructor(
        public dataService: VisualsDashboardDataService<QualityScoreHistoryDataResponse>,
        private translate: TranslateService,
    ) {
        effect(() => {
            const widgetId = this.widgetId();
            const periodValue = this.pickedPeriodValue();
            if (widgetId !== null && periodValue !== null) {
                this.dataService.load(widgetId, periodValue);
            }
        });
    }
}
